import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { Loader2, Send, Bot, User, Sparkles, Crown, MessageCircle, Target, Settings, Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AssistantPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [activeContext, setActiveContext] = useState(
    () => localStorage.getItem('assistant_context') || 'chat'
  );
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [appliedIds, setAppliedIds] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;
    axios
      .get(`${API}/assistant/history?limit=100&context=${activeContext}`, { headers: getAuthHeaders() })
      .then(res => setConversations(res.data.conversations.reverse()))
      .catch(err => console.error('Error fetching history:', err));
  }, [activeContext, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [conversations, activeContext]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      const [planRes, historyRes, plansRes] = await Promise.all([
        axios.get(`${API}/user/plan`, { headers: getAuthHeaders() }),
        axios.get(`${API}/assistant/history?limit=100&context=${activeContext}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/plans`, { headers: getAuthHeaders() })
      ]);
      setPlanInfo(planRes.data);
      setConversations(historyRes.data.conversations.reverse());
      setAvailablePlans(plansRes.data.plans);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    const userMessage = message;
    setMessage('');
    setSending(true);

    // Optimistically add user message
    const tempId = `temp-${Date.now()}`;
    setConversations(prev => [...prev, {
      id: tempId,
      user_message: userMessage,
      assistant_message: null,
      context: activeContext,
      created_at: new Date().toISOString()
    }]);

    try {
      const response = await axios.post(
        `${API}/assistant/chat`,
        { message: userMessage, context: activeContext },
        { headers: getAuthHeaders() }
      );

      // Replace temp message with real one
      setConversations(prev => 
        prev.map(c => c.id === tempId ? response.data : c)
      );

      // Update plan info for message count
      const planRes = await axios.get(`${API}/user/plan`, { headers: getAuthHeaders() });
      setPlanInfo(planRes.data);

    } catch (error) {
      // Remove temp message on error
      setConversations(prev => prev.filter(c => c.id !== tempId));
      
      if (error.response?.status === 429) {
        toast.error('Limite diário atingido. Faz upgrade para continuar!');
        setUpgradeDialogOpen(true);
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        setUpgradeDialogOpen(true);
      } else {
        toast.error('Erro ao enviar mensagem');
      }
    } finally {
      setSending(false);
    }
  };

  const handleUpgrade = (planId) => {
    // Paid upgrades must go through the real Stripe checkout on the Pricing page
    setUpgradeDialogOpen(false);
    navigate('/pricing');
  };

  const contextOptions = [
    { 
      id: 'chat', 
      label: 'Chatbot', 
      icon: MessageCircle, 
      description: 'Perguntas sobre treino',
      plan: 'free'
    },
    { 
      id: 'coach', 
      label: 'Coach Virtual', 
      icon: Target, 
      description: 'Dicas personalizadas',
      plan: 'pro'
    },
    { 
      id: 'adjustment', 
      label: 'Ajustar Plano', 
      icon: Settings, 
      description: 'Modificar o teu plano',
      plan: 'premium'
    }
  ];

  const selectContext = (contextId) => {
    setActiveContext(contextId);
    localStorage.setItem('assistant_context', contextId);
  };

  // Each tab keeps its own separate conversation
  const visibleConversations = conversations.filter(
    (c) => (c.context || 'chat') === activeContext
  );

  const handleApplyAdjustment = async (conversationId) => {
    setApplyingId(conversationId);
    try {
      const response = await axios.post(
        `${API}/assistant/apply-adjustment`,
        { conversation_id: conversationId },
        { headers: getAuthHeaders() }
      );
      setAppliedIds(prev => [...prev, conversationId]);
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, applied_plan_id: response.data.id } : c
      ));
      toast.success('Plano ajustado e guardado no Histórico!', {
        action: {
          label: 'Ver plano',
          onClick: () => navigate(`/history/${response.data.id}`)
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar o ajuste');
    } finally {
      setApplyingId(null);
    }
  };

  const canUseContext = (contextPlan) => {
    const planHierarchy = { free: 0, pro: 1, premium: 2 };
    const userPlanLevel = planHierarchy[planInfo?.plan || 'free'];
    const requiredLevel = planHierarchy[contextPlan];
    return userPlanLevel >= requiredLevel;
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('pt-PT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="assistant-page" className="max-w-5xl mx-auto h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)] flex flex-col">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 
              className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Assistente <span className="text-[#32CD32]">IA</span>
            </h1>
            <p className="text-gray-400">
              O teu personal trainer virtual 24/7
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Plan Badge */}
            <div className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${
              planInfo?.plan === 'premium' ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black' :
              planInfo?.plan === 'pro' ? 'bg-[#32CD32]/20 text-[#32CD32]' :
              'bg-gray-800 text-gray-400'
            }`}>
              {planInfo?.plan === 'premium' && <Crown className="w-4 h-4" />}
              {planInfo?.plan === 'pro' && <Sparkles className="w-4 h-4" />}
              {planInfo?.plan_name}
            </div>
            
            {/* Message Count */}
            {planInfo?.daily_messages_limit !== -1 ? (
              <div className="text-sm text-gray-500" data-testid="message-counter">
                {planInfo?.daily_messages_used}/{planInfo?.daily_messages_limit} msgs hoje
              </div>
            ) : (
              <div className="text-sm text-gray-500" data-testid="message-counter">
                Mensagens ilimitadas
              </div>
            )}
            
            {/* Upgrade Button */}
            {planInfo?.plan !== 'premium' && (
              <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#32CD32]/50 text-[#32CD32] hover:bg-[#32CD32]/10" data-testid="upgrade-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Upgrade
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
                      Escolhe o teu Plano
                    </DialogTitle>
                    <DialogDescription>
                      Desbloqueia recursos avançados do assistente IA
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {availablePlans.map((plan) => (
                      <div 
                        key={plan.id}
                        className={`p-4 rounded-xl border ${
                          planInfo?.plan === plan.id 
                            ? 'border-[#32CD32] bg-[#32CD32]/10' 
                            : 'border-white/10 bg-black/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold">{plan.name}</h3>
                          {plan.id === 'premium' && <Crown className="w-5 h-5 text-yellow-500" />}
                        </div>
                        <p className="text-2xl font-bold mb-4">
                          {plan.price === 0 ? 'Grátis' : `€${plan.price.toFixed(2)}`}
                          {plan.price > 0 && <span className="text-sm text-gray-500">/mês</span>}
                        </p>
                        <ul className="space-y-2 mb-4">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                              <Check className="w-4 h-4 text-[#32CD32]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <Button 
                          className={`w-full ${planInfo?.plan === plan.id ? 'bg-gray-700' : 'btn-primary'}`}
                          disabled={planInfo?.plan === plan.id}
                          onClick={() => handleUpgrade(plan.id)}
                          data-testid={`select-plan-${plan.id}`}
                        >
                          {planInfo?.plan === plan.id ? 'Plano Atual' : 'Selecionar'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Context Selector */}
        <div className="tab-scroll mb-4">
          {contextOptions.map((ctx) => {
            const Icon = ctx.icon;
            const canUse = canUseContext(ctx.plan);
            return (
              <button
                key={ctx.id}
                onClick={() => canUse ? selectContext(ctx.id) : setUpgradeDialogOpen(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                  activeContext === ctx.id
                    ? 'bg-[#32CD32] text-black'
                    : canUse
                    ? 'bg-black/30 border border-white/10 hover:border-[#32CD32]/50'
                    : 'bg-black/30 border border-white/10 opacity-50'
                }`}
                data-testid={`context-${ctx.id}`}
              >
                {!canUse && <Lock className="w-4 h-4" />}
                <Icon className="w-4 h-4" />
                <span className="font-medium">{ctx.label}</span>
              </button>
            );
          })}
        </div>

        {/* Chat Area */}
        <Card className="flex-1 bg-[#121212] border-white/10 flex flex-col overflow-hidden">
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
            {visibleConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Bot className="w-16 h-16 text-[#32CD32]/50 mb-4" />
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  Olá! Sou o teu assistente de treino
                </h3>
                <p className="text-[#A1A1AA] max-w-md">
                  {activeContext === 'chat' && 'Pergunta qualquer coisa sobre exercícios, técnicas ou nutrição para cardio.'}
                  {activeContext === 'coach' && 'Vou analisar o teu perfil e progresso para dar dicas personalizadas.'}
                  {activeContext === 'adjustment' && 'Posso sugerir ajustes no teu plano de treino atual.'}
                </p>
              </div>
            ) : (
              visibleConversations.map((conv) => (
                <div key={conv.id} className="space-y-3">
                  {/* User Message */}
                  <div className="flex justify-end">
                    <div className="max-w-[80%] bg-[#32CD32]/20 rounded-2xl rounded-tr-sm p-4">
                      <p className="text-sm">{conv.user_message}</p>
                      <span className="text-xs text-gray-500 mt-1 block">{formatTime(conv.created_at)}</span>
                    </div>
                  </div>
                  
                  {/* Assistant Message */}
                  {conv.assistant_message ? (
                    <div className="flex justify-start">
                      <div className="flex gap-3 max-w-[80%]">
                        <div className="w-8 h-8 rounded-full bg-[#32CD32]/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-[#32CD32]" />
                        </div>
                        <div className="bg-black/30 rounded-2xl rounded-tl-sm p-4">
                          <p className="text-sm whitespace-pre-wrap">{conv.assistant_message.replace(/[*#]/g, '').replace(/\n{3,}/g, '\n\n')}</p>
                          {conv.context === 'adjustment' && !String(conv.id).startsWith('temp-') && (
                            <Button
                              onClick={() => handleApplyAdjustment(conv.id)}
                              disabled={applyingId === conv.id || appliedIds.includes(conv.id) || !!conv.applied_plan_id}
                              className="btn-primary mt-4 h-9 rounded-lg text-xs"
                              data-testid={`apply-adjustment-btn-${conv.id}`}
                            >
                              {applyingId === conv.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  A aplicar...
                                </>
                              ) : (appliedIds.includes(conv.id) || conv.applied_plan_id) ? (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Aplicado ao plano
                                </>
                              ) : (
                                <>
                                  <Settings className="w-4 h-4 mr-2" />
                                  Aplicar ao meu plano
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div className="flex gap-3 max-w-[80%]">
                        <div className="w-8 h-8 rounded-full bg-[#32CD32]/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-[#32CD32]" />
                        </div>
                        <div className="bg-black/30 rounded-2xl rounded-tl-sm p-4">
                          <Loader2 className="w-5 h-5 animate-spin text-[#32CD32]" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Input Area */}
          <div className="p-4 border-t border-white/10">
            <form onSubmit={handleSend} className="flex gap-3">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  activeContext === 'chat' ? 'Pergunta sobre treino, técnicas, nutrição...' :
                  activeContext === 'coach' ? 'Pede dicas personalizadas ou motivação...' :
                  'Descreve o que gostarias de ajustar no teu plano...'
                }
                className="flex-1 h-12 bg-black/50 border-white/20 rounded-xl"
                disabled={sending}
                data-testid="assistant-input"
              />
              <Button 
                type="submit" 
                className="btn-primary h-12 px-6 rounded-xl"
                disabled={sending || !message.trim()}
                data-testid="send-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AssistantPage;
